import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
import { Package } from '@/common/models';

/**
 * Determines if a package can be selected based on the currently selected packages.
 * @param item - The package to verify
 * @param selectedItems - The selected packages
 */
export function isItemSelectable(item: Package, selectedItems: Package[]): boolean {
  // If no packages are currently selected, then any package can be selected.
  if (selectedItems.length === 0) return true;
  const selectedItem: Package = selectedItems[0];
  // If the currently select item is a curated deal/has demographic targeting/is guaranteed, then we can only modify that same item.
  if (
    selectedItem.isCuratedDeal ||
    selectedItem.hasAudienceTargeting ||
    selectedItem.purchaseType === PurchaseTypesEnum.GUARANTEED
  ) {
    return item.id === selectedItem.id;
  }
  // Curated deals and publisher deals cannot be bought together just yet (HSSP-3253)
  const isMismatchedDealType = !selectedItem.isCuratedDeal && item.isCuratedDeal;
  // If the item doesn't have demographic targeting, then we can select it.
  return !item.hasAudienceTargeting && item.purchaseType !== PurchaseTypesEnum.GUARANTEED && !isMismatchedDealType;
}

/**
 * Simple helper function to determine if a package is selected.
 * @param item - The package to verify
 * @param selectedItems - The selected packages
 */
export function isItemSelected(item: Package, selectedItems: Package[]): boolean {
  return selectedItems.some((selectedItem: Package) => selectedItem.id === item.id);
}

/**
 * Timeout to delay the change of the active deal to ensure a seamless transition.
 * @param deal - The updated deal to display.
 * @param activeDeal - The currently active deal.
 * @param mutation - The mutation function to update the active deal.
 */
export function onDealPreviewChanged(deal: Package, activeDeal: Package, mutation: (deal: Package) => void) {
  const delay = activeDeal ? 200 : 0;
  if (activeDeal?.id !== deal?.id) {
    mutation(null);
    setTimeout(() => {
      mutation(deal);
    }, delay);
  } else {
    mutation(null);
  }
}

export enum DealScope {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export function getDealScopeType(value: boolean): DealScope | null {
  if (value == null) return null;
  return value ? DealScope.EXTERNAL : DealScope.INTERNAL;
}
